import { useState } from "react";
import { useLocation } from "wouter";
import { 
  FolderOpen, 
  Search, 
  Copy, 
  ArrowRight,
  FileText,
  BarChart3,
  Users,
  Target,
  Briefcase
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { reportTemplates } from "@/lib/report-templates";

// Convert report templates to a more structured format
const templates = Object.entries(reportTemplates).map(([key, template]) => {
  const category = getCategoryForTemplate(key);
  return {
    id: key,
    title: template.name,
    prompt: template.prompt,
    category: category,
    description: template.description || `Generate a comprehensive ${template.name.toLowerCase()} report`,
    icon: getIconForTemplate(key),
    color: getColorForCategory(category)
  };
});

function getCategoryForTemplate(key: string) {
  if (key.includes('market') || key.includes('analysis')) return "Market Research";
  if (key.includes('customer') || key.includes('user')) return "Research";
  if (key.includes('strategy') || key.includes('plan')) return "Strategy";
  if (key.includes('business') || key.includes('model')) return "Business";
  if (key.includes('competitive')) return "Analysis";
  return "Business";
}

function getIconForTemplate(key: string) {
  if (key.includes('market') || key.includes('analysis')) return BarChart3;
  if (key.includes('customer') || key.includes('user')) return Users;
  if (key.includes('strategy') || key.includes('plan')) return Target;
  if (key.includes('business') || key.includes('model')) return Briefcase;
  return FileText;
}

function getColorForCategory(category: string) {
  const colors = {
    'Business': 'blue',
    'Market Research': 'green',
    'Strategy': 'purple',
    'Analysis': 'red',
    'Planning': 'yellow',
    'Research': 'indigo'
  };
  return colors[category as keyof typeof colors] || 'blue';
}

export default function TemplatesPage() {
  const [, setLocation] = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const categories = ["all", ...Array.from(new Set(templates.map(t => t.category)))];

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.prompt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === "all" || template.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const getColorClasses = (color: string) => {
    const colors = {
      blue: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      green: "bg-green-500/20 text-green-400 border-green-500/30",
      purple: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      red: "bg-red-500/20 text-red-400 border-red-500/30",
      yellow: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      indigo: "bg-indigo-500/20 text-indigo-400 border-indigo-500/30"
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  const useTemplate = (prompt: string) => {
    // Store the template in localStorage to be picked up by the main chat
    localStorage.setItem("veritus-selected-prompt", prompt);
    setLocation("/");
  };

  const copyTemplate = (prompt: string) => {
    navigator.clipboard.writeText(prompt);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur-md">
        <div className="container flex h-16 items-center justify-between px-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setLocation("/")}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                <span className="text-white font-bold text-sm">T</span>
              </div>
              <span className="font-semibold text-lg">AcronIQ Veritus</span>
            </button>
            <div className="h-6 w-px bg-border mx-2" />
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <FolderOpen className="h-5 w-5" />
              Report Templates
            </h1>
          </div>

          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation("/signals")}
              className="text-muted-foreground hover:text-foreground"
            >
              Signals
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation("/reports")}
              className="text-muted-foreground hover:text-foreground"
            >
              Reports
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation("/")}
              className="text-muted-foreground hover:text-foreground"
            >
              Back to Chat
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-8 py-8">
        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <div className="flex gap-2 flex-wrap">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                className="capitalize"
              >
                {category === "all" ? "All Categories" : category}
              </Button>
            ))}
          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => {
            const IconComponent = template.icon;
            return (
              <Card key={template.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getColorClasses(template.color)}`}>
                        <IconComponent className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{template.title}</CardTitle>
                        <Badge variant="secondary" className="text-xs mt-1">
                          {template.category}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    {template.description}
                  </p>
                  
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground line-clamp-3">
                      {template.prompt}
                    </p>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => useTemplate(template.prompt)}
                      className="flex-1"
                      size="sm"
                    >
                      <ArrowRight className="h-4 w-4 mr-2" />
                      Use Template
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyTemplate(template.prompt)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredTemplates.length === 0 && (
          <div className="text-center py-12">
            <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No templates found</h3>
            <p className="text-muted-foreground">
              Try adjusting your search terms or category filter
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
